import VirtualBackgroundExtension, {
  IVirtualBackgroundProcessor,
} from 'agora-extension-virtual-background'
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-sdk-ng'
import { fetchRoomToken } from 'services/VideoCall/fetchRoomToken'
import { DeviceList, NetworkQualityInfo, RemoteUserState } from '../types'

const RECONNECT_LOG_PREFIX = '[VideoCallMeet][reconnect]'

const CHANNEL_MODE: 'rtc' | 'live' = 'rtc'
const IS_LIVE_MODE: boolean = (CHANNEL_MODE as string) === 'live'

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  attempts = 3,
  baseDelayMs = 500,
  label = 'op'
): Promise<T> {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      console.error(
        `${RECONNECT_LOG_PREFIX} ${label} attempt ${i + 1}/${attempts} failed`,
        err
      )
      if (i < attempts - 1) await sleep(baseDelayMs * 2 ** i)
    }
  }
  throw lastErr
}

export interface AgoraEngineListeners {
  onRemoteUsersChanged?: (users: RemoteUserState[]) => void
  onConnectionStateChanged?: (state: string) => void
  onNetworkQuality?: (quality: NetworkQualityInfo) => void
  onTokenWillExpire?: () => void
  onTokenDidExpire?: () => void
  onError?: (error: Error) => void
}

/** Resultado do leave: nunca rejeita, mas informa se sair do canal falhou. */
export type LeaveResult = { ok: true } | { ok: false; error: Error }

type LocalTracks = {
  audioTrack: IMicrophoneAudioTrack | null
  videoTrack: ICameraVideoTrack | null
}

const CONNECTION_TIMEOUT_MS = 8000

function mapRemoteUsers(users: IAgoraRTCRemoteUser[]): RemoteUserState[] {
  return users.map((user) => ({
    uid: String(user.uid),
    audioTrack: user.audioTrack,
    videoTrack: user.videoTrack,
    hasAudio: !!user.audioTrack,
    hasVideo: !!user.videoTrack,
  }))
}

export class AgoraEngine {
  private client: IAgoraRTCClient
  private localTracks: LocalTracks
  private listeners: AgoraEngineListeners
  private extension: VirtualBackgroundExtension
  private processor: IVirtualBackgroundProcessor | null
  private processorTrack: ICameraVideoTrack | null
  private listenersBound: boolean
  private autoplayBlocked: boolean
  private pendingAudioTracks: any[]
  private schedulingId: string | null
  private currentUid: number | null
  private reconnectInFlight: boolean
  private lifecycleQueue: Promise<unknown>
  private sessionGeneration: number
  private creatingAudioTrack: IMicrophoneAudioTrack | null
  private pendingSubscriptions: {
    user: IAgoraRTCRemoteUser
    mediaType: 'audio' | 'video'
  }[]

  constructor() {
    this.client = AgoraRTC.createClient({ mode: CHANNEL_MODE, codec: 'h264' })
    this.localTracks = {
      audioTrack: null,
      videoTrack: null,
    }
    this.listeners = {}
    this.extension = new VirtualBackgroundExtension()
    this.processor = null
    this.processorTrack = null
    this.listenersBound = false
    this.autoplayBlocked = false
    this.pendingAudioTracks = []
    this.schedulingId = null
    this.currentUid = null
    this.reconnectInFlight = false
    this.lifecycleQueue = Promise.resolve()
    this.sessionGeneration = 0
    this.creatingAudioTrack = null
    this.pendingSubscriptions = []

    AgoraRTC.registerExtensions([this.extension])
  }

  setListeners(listeners: AgoraEngineListeners) {
    this.listeners = listeners
    this.bindClientListeners()
  }

  getClient() {
    return this.client
  }

  getLocalTracks() {
    return this.localTracks
  }

  async listDevices(): Promise<DeviceList> {
    const allDevices = await AgoraRTC.getDevices()
    return {
      cameras: allDevices.filter((device) => device.kind === 'videoinput'),
      microphones: allDevices.filter((device) => device.kind === 'audioinput'),
    }
  }

  /**
   * Serializa join/rejoin/recover. Sem isso, dois joins concorrentes no
   * singleton criam duas camera tracks e o SDK rejeita o publish com
   * CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS.
   * O leave não entra na fila: incrementa `sessionGeneration`, fecha as tracks
   * e sai do canal na hora (aborta um client.join pendente e não espera
   * ensureConnected, o prompt de permissão da câmera, publish nem backoff de
   * reconexão). A fila recomeça a partir do leave, então um join seguinte não
   * espera a operação da sessão encerrada; essa operação checa
   * `isStale(generation)` depois de cada await e desiste sem tocar no client.
   */
  private runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.lifecycleQueue.then(fn, fn)
    this.lifecycleQueue = run.catch(() => undefined)
    return run
  }

  /** Resolve `true` se publicou as tracks, `false` se um leave cancelou o join. */
  joinRoom(
    channel: string,
    token: string,
    uid: number,
    schedulingId?: string
  ): Promise<boolean> {
    const generation = this.sessionGeneration
    return this.runExclusive(() =>
      this.doJoinRoom(channel, token, uid, schedulingId, generation)
    )
  }

  /**
   * Nunca rejeita: o estado local é zerado de qualquer forma. Se o
   * client.leave falhar, resolve `{ ok: false, error }` para a UI registrar.
   */
  leaveRoom(): Promise<LeaveResult> {
    this.sessionGeneration++
    const leave = this.doLeaveRoom()
    this.lifecycleQueue = leave
    return leave
  }

  private isStale(generation: number) {
    return generation !== this.sessionGeneration
  }

  private async doJoinRoom(
    channel: string,
    token: string,
    uid: number,
    schedulingId: string | undefined,
    generation: number
  ): Promise<boolean> {
    const appId = process.env.REACT_APP_AGORA_IO_APP_ID
    if (!appId) {
      throw new Error('REACT_APP_AGORA_IO_APP_ID não configurado')
    }

    // Um leave chamado depois deste join encerra a sessão; publicar depois
    // dele abriria a câmera para o paciente após o profissional já ter saído.
    if (this.isStale(generation)) return false

    if (
      this.client.connectionState !== 'DISCONNECTED' ||
      this.localTracks.audioTrack ||
      this.localTracks.videoTrack
    ) {
      await this.doLeaveRoom()
    }

    // Um leave pode ter chegado durante o doLeaveRoom acima, antes de existir
    // client.join para abortar.
    if (this.isStale(generation)) return false

    if (schedulingId) this.schedulingId = schedulingId
    this.currentUid = uid

    try {
      return await this.connectAndPublish(
        appId,
        channel,
        token,
        uid,
        generation
      )
    } catch (e) {
      // Join abortado pelo leaveRoom: não é falha para a UI. A limpeza é do
      // leave; repetir aqui poderia derrubar uma sessão nova.
      if (this.isStale(generation)) return false
      // Falha real (permissão negada, timeout, publish): não deixar o
      // profissional no canal sem mídia nem câmera/microfone abertos.
      await this.doLeaveRoom()
      throw e
    }
  }

  private async connectAndPublish(
    appId: string,
    channel: string,
    token: string,
    uid: number,
    generation: number
  ): Promise<boolean> {
    await this.client.join(appId, channel, token, uid)
    await this.ensureConnected()
    if (this.isStale(generation)) return false

    let audioTrack: IMicrophoneAudioTrack | null = null
    let videoTrack: ICameraVideoTrack
    try {
      audioTrack = await AgoraRTC.createMicrophoneAudioTrack()
      // Leave durante o prompt do microfone: não abrir a câmera nem tomar o
      // creatingAudioTrack de um join da sessão nova.
      if (this.isStale(generation)) {
        audioTrack.close()
        return false
      }
      // Exposto para o leaveRoom fechar o microfone durante o prompt da câmera.
      this.creatingAudioTrack = audioTrack
      videoTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: {
          width: 640,
          height: 360,
          frameRate: 15,
          bitrateMin: 200,
          bitrateMax: 800,
        },
      })
    } catch (e) {
      audioTrack?.close()
      throw e
    } finally {
      if (this.creatingAudioTrack === audioTrack) this.creatingAudioTrack = null
    }

    if (this.isStale(generation)) {
      audioTrack.close()
      videoTrack.close()
      return false
    }
    this.localTracks.audioTrack = audioTrack
    this.localTracks.videoTrack = videoTrack

    await this.ensureVBProcessor()
    if (this.isStale(generation)) return false

    // Defesa: uma video track de sessão anterior ainda publicada faria o SDK
    // rejeitar com CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS.
    await this.unpublishStaleVideo(videoTrack)
    if (this.isStale(generation)) return false

    await this.client.publish([audioTrack, videoTrack])
    // Leave durante o publish: a sessão já foi encerrada, não é join concluído.
    return !this.isStale(generation)
  }

  // O estado local é zerado de forma síncrona antes do primeiro await: o leave
  // não entra na fila, e uma operação concorrente não pode ver tracks já
  // fechadas nem tracks da sessão seguinte. O client.leave é a primeira
  // chamada assíncrona para sair do canal (e abortar um join) na hora.
  private async doLeaveRoom(): Promise<LeaveResult> {
    const { audioTrack, videoTrack } = this.localTracks
    const creatingAudioTrack = this.creatingAudioTrack
    const processor = this.processor
    const processorTrack = this.processorTrack

    this.localTracks.audioTrack = null
    this.localTracks.videoTrack = null
    this.creatingAudioTrack = null
    this.processor = null
    this.processorTrack = null
    this.pendingAudioTracks = []
    this.autoplayBlocked = false
    this.schedulingId = null
    this.currentUid = null
    this.reconnectInFlight = false
    this.pendingSubscriptions = []

    for (const track of [audioTrack, videoTrack, creatingAudioTrack]) {
      if (!track) continue
      try {
        track.stop()
        track.close()
      } catch (e) {
        console.warn(`${RECONNECT_LOG_PREFIX} close track failed`, e)
      }
    }

    let result: LeaveResult = { ok: true }
    if (this.client.connectionState !== 'DISCONNECTED') {
      try {
        await this.client.leave()
      } catch (e) {
        console.error(`${RECONNECT_LOG_PREFIX} client.leave failed`, e)
        result = {
          ok: false,
          error: e instanceof Error ? e : new Error(String(e)),
        }
      }
    }

    await this.releaseVBProcessor(processor, processorTrack)
    return result
  }

  async toggleAudio() {
    if (!this.localTracks.audioTrack) return true
    const isMuted = this.localTracks.audioTrack.muted
    await this.localTracks.audioTrack.setMuted(!isMuted)
    return !this.localTracks.audioTrack.muted
  }

  async toggleVideo() {
    if (!this.localTracks.videoTrack) return false
    const enabled = this.localTracks.videoTrack.enabled
    await this.localTracks.videoTrack.setEnabled(!enabled)
    return this.localTracks.videoTrack.enabled
  }

  async switchCamera(cameraId: string) {
    if (!this.localTracks.videoTrack) return
    await this.localTracks.videoTrack.setDevice(cameraId)
  }

  async switchMicrophone(microphoneId: string) {
    if (!this.localTracks.audioTrack) return
    await this.localTracks.audioTrack.setDevice(microphoneId)
  }

  async setVirtualBackgroundNone() {
    await this.disableVirtualBackground()
  }

  async setVirtualBackgroundBlur() {
    const processor = await this.ensureVBProcessor()
    if (!processor) return

    processor.setOptions({ type: 'blur', blurDegree: 3 })
    await processor.enable()
  }

  async setVirtualBackgroundImage(source: string) {
    const processor = await this.ensureVBProcessor()
    if (!processor) return

    const imageElement = document.createElement('img')
    imageElement.src = source
    await new Promise<void>((resolve, reject) => {
      imageElement.onload = () => resolve()
      imageElement.onerror = () =>
        reject(new Error('Erro ao carregar imagem de fundo virtual'))
    })

    processor.setOptions({ type: 'img', source: imageElement })
    await processor.enable()
  }

  private playRemoteAudio(track: any) {
    if (!track) return
    try {
      track.play()
    } catch {
      this.scheduleAudioRetry(track)
    }
  }

  private scheduleAudioRetry(track: any) {
    this.pendingAudioTracks.push(track)
    if (this.autoplayBlocked) return
    this.autoplayBlocked = true

    const onInteract = () => {
      const queued = [...this.pendingAudioTracks]
      this.pendingAudioTracks = []
      queued.forEach((t) => {
        try {
          t.play()
        } catch {
          // ignore
        }
      })
      this.autoplayBlocked = false
      window.removeEventListener('pointerdown', onInteract)
      window.removeEventListener('keydown', onInteract)
    }

    window.addEventListener('pointerdown', onInteract, { once: true })
    window.addEventListener('keydown', onInteract, { once: true })
  }

  private bindClientListeners() {
    if (this.listenersBound) return

    this.listenersBound = true
    this.client.on('user-published', async (user, mediaType) => {
      if (mediaType !== 'audio' && mediaType !== 'video') return
      await this.trySubscribe(user, mediaType)
    })

    this.client.on('user-unpublished', () => {
      this.listeners.onRemoteUsersChanged?.(
        mapRemoteUsers(this.client.remoteUsers)
      )
    })

    this.client.on('user-left', () => {
      this.listeners.onRemoteUsersChanged?.(
        mapRemoteUsers(this.client.remoteUsers)
      )
    })

    this.client.on('connection-state-change', (curState, revState, reason) => {
      this.listeners.onConnectionStateChanged?.(curState)
      console.log('connection-state-change', curState, revState, reason)

      if (curState === 'CONNECTED' && revState === 'RECONNECTING') {
        console.warn(
          `${RECONNECT_LOG_PREFIX} reconnected (reason=${reason ?? 'n/a'}), restoring publish state`
        )
        const generation = this.sessionGeneration
        void this.runExclusive(() => this.recoverAfterReconnect(generation))
      } else if (curState === 'CONNECTED') {
        void this.drainPendingSubscriptions()
      }
    })

    this.client.on('network-quality', (stats) => {
      const txQuality = Number(stats?.uplinkNetworkQuality || 0)
      const rxQuality = Number(stats?.downlinkNetworkQuality || 0)
      const overallQuality = Math.max(txQuality, rxQuality)
      this.listeners.onNetworkQuality?.({
        txQuality,
        rxQuality,
        overallQuality,
        timestamp: Date.now(),
      })
    })

    this.client.on('token-privilege-will-expire', () => {
      this.listeners.onTokenWillExpire?.()
      void this.renewTokenProactive()
    })

    this.client.on('token-privilege-did-expire', () => {
      this.listeners.onTokenDidExpire?.()
      void this.rejoinWithFreshToken()
    })
  }

  private async recoverAfterReconnect(generation: number) {
    if (this.reconnectInFlight || this.isStale(generation)) return
    this.reconnectInFlight = true
    try {
      if (IS_LIVE_MODE) {
        try {
          await retryWithBackoff(
            () => this.client.setClientRole('host'),
            3,
            500,
            'setClientRole'
          )
        } catch (e) {
          console.error(`${RECONNECT_LOG_PREFIX} setClientRole failed`, e)
        }
      }

      await this.republishLocalTracks(generation)
      if (this.isStale(generation)) return
      await this.drainPendingSubscriptions()
    } finally {
      // Um recover stale não libera a guarda de um recover da sessão nova.
      if (!this.isStale(generation)) this.reconnectInFlight = false
    }
  }

  private queueSubscribe(
    user: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video'
  ) {
    const alreadyQueued = this.pendingSubscriptions.some(
      (pending) =>
        pending.user.uid === user.uid && pending.mediaType === mediaType
    )
    if (!alreadyQueued) {
      this.pendingSubscriptions.push({ user, mediaType })
    }
  }

  private async drainPendingSubscriptions() {
    if (!this.pendingSubscriptions.length) return
    if (this.client.connectionState !== 'CONNECTED') return

    const pending = this.pendingSubscriptions
    this.pendingSubscriptions = []

    for (const { user, mediaType } of pending) {
      const stillRemote = this.client.remoteUsers.find(
        (remoteUser) => remoteUser.uid === user.uid
      )
      if (!stillRemote) continue
      await this.trySubscribe(stillRemote, mediaType)
    }
  }

  private async trySubscribe(
    user: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video'
  ) {
    if (this.client.connectionState !== 'CONNECTED') {
      console.warn(
        `${RECONNECT_LOG_PREFIX} subscribe skipped for uid=${user.uid} mediaType=${mediaType}, connectionState=${this.client.connectionState}; queued for retry`
      )
      this.queueSubscribe(user, mediaType)
      this.listeners.onRemoteUsersChanged?.(
        mapRemoteUsers(this.client.remoteUsers)
      )
      return
    }

    try {
      await this.client.subscribe(user, mediaType)

      if (mediaType === 'audio' && user.audioTrack) {
        this.playRemoteAudio(user.audioTrack)
      }
    } catch (error) {
      console.error(
        `${RECONNECT_LOG_PREFIX} subscribe failed for uid=${user.uid} mediaType=${mediaType}`,
        error
      )
      this.queueSubscribe(user, mediaType)
      this.listeners.onError?.(error as Error)
    } finally {
      this.listeners.onRemoteUsersChanged?.(
        mapRemoteUsers(this.client.remoteUsers)
      )
    }
  }

  private async unpublishStaleVideo(current: ICameraVideoTrack | null) {
    const published = this.client.localTracks ?? []
    const staleVideo = published.filter(
      (t) => t.trackMediaType === 'video' && t !== current
    )
    if (!staleVideo.length) return
    console.warn(
      `${RECONNECT_LOG_PREFIX} unpublishing ${staleVideo.length} stale video track(s)`
    )
    try {
      await this.client.unpublish(staleVideo)
    } catch (e) {
      console.error(`${RECONNECT_LOG_PREFIX} unpublish stale video failed`, e)
    }
  }

  private async republishLocalTracks(generation: number) {
    if (this.client.connectionState !== 'CONNECTED') return
    if (this.isStale(generation)) return

    const desired: (IMicrophoneAudioTrack | ICameraVideoTrack)[] = []
    if (this.localTracks.audioTrack) desired.push(this.localTracks.audioTrack)
    if (this.localTracks.videoTrack) desired.push(this.localTracks.videoTrack)
    if (!desired.length) return

    await this.unpublishStaleVideo(this.localTracks.videoTrack)
    if (this.isStale(generation)) return

    const alreadyPublished = new Set(this.client.localTracks ?? [])
    const toPublish = desired.filter((t) => !alreadyPublished.has(t))
    if (!toPublish.length) {
      console.warn(`${RECONNECT_LOG_PREFIX} no tracks to republish`)
      return
    }

    try {
      await retryWithBackoff(
        async () => {
          // Leave durante os retries: não republicar a sessão encerrada.
          if (this.isStale(generation)) return
          await this.client.publish(toPublish)
        },
        3,
        500,
        'publish'
      )

      if (this.isStale(generation)) return

      if (this.localTracks.videoTrack) {
        try {
          await this.ensureVBProcessor()
        } catch (e) {
          console.error(
            `${RECONNECT_LOG_PREFIX} ensureVBProcessor failed after republish`,
            e
          )
        }
      }

      console.warn(`${RECONNECT_LOG_PREFIX} tracks republished`)
    } catch (e) {
      console.error(`${RECONNECT_LOG_PREFIX} republish failed`, e)
      this.listeners.onError?.(e as Error)
    }
  }

  private async renewTokenProactive() {
    const generation = this.sessionGeneration
    const schedulingId = this.schedulingId
    if (!schedulingId) {
      console.error(`${RECONNECT_LOG_PREFIX} renewToken missing schedulingId`)
      return
    }
    try {
      const session = await retryWithBackoff(
        () => fetchRoomToken(schedulingId),
        3,
        500,
        'fetchRoomToken'
      )
      // Sala trocada durante o fetch: o token é de outro canal.
      if (this.isStale(generation) || this.schedulingId !== schedulingId) {
        console.warn(`${RECONNECT_LOG_PREFIX} renewToken aborted, room changed`)
        return
      }
      await this.client.renewToken(session.token)
      console.warn(`${RECONNECT_LOG_PREFIX} token renewed`)
    } catch (e) {
      console.error(`${RECONNECT_LOG_PREFIX} renewToken failed`, e)
    }
  }

  // O fetch do token fica fora da fila para não segurar um leave do usuário
  // (até ~90s com timeouts de rede); só o leave/join do client é serializado.
  private async rejoinWithFreshToken() {
    const generation = this.sessionGeneration
    const schedulingId = this.schedulingId
    const uid = this.currentUid
    if (!schedulingId || uid == null) {
      console.error(`${RECONNECT_LOG_PREFIX} rejoin missing schedulingId/uid`)
      return
    }
    const appId = process.env.REACT_APP_AGORA_IO_APP_ID
    if (!appId) {
      console.error(`${RECONNECT_LOG_PREFIX} rejoin missing appId`)
      return
    }

    let session: Awaited<ReturnType<typeof fetchRoomToken>>
    try {
      session = await retryWithBackoff(
        () => fetchRoomToken(schedulingId),
        3,
        500,
        'fetchRoomToken'
      )
    } catch (e) {
      console.error(`${RECONNECT_LOG_PREFIX} rejoin failed`, e)
      this.listeners.onError?.(e as Error)
      return
    }

    await this.runExclusive(async () => {
      if (
        this.isStale(generation) ||
        this.schedulingId !== schedulingId ||
        this.currentUid !== uid
      ) {
        console.warn(`${RECONNECT_LOG_PREFIX} rejoin aborted, room changed`)
        return
      }
      try {
        try {
          if (this.client.connectionState !== 'DISCONNECTED') {
            await this.client.leave()
          }
        } catch (e) {
          console.warn(`${RECONNECT_LOG_PREFIX} leave before rejoin failed`, e)
        }
        if (this.isStale(generation)) return
        await this.client.join(appId, session.channelName, session.token, uid)
        await this.republishLocalTracks(generation)
        console.warn(`${RECONNECT_LOG_PREFIX} rejoined with fresh token`)
      } catch (e) {
        if (this.isStale(generation)) return
        console.error(`${RECONNECT_LOG_PREFIX} rejoin failed`, e)
        this.listeners.onError?.(e as Error)
      }
    })
  }

  private async ensureConnected() {
    if (this.client.connectionState === 'CONNECTED') return

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.client.off('connection-state-change', handleState)
        reject(new Error('Timeout ao conectar na sala'))
      }, CONNECTION_TIMEOUT_MS)

      const handleState = (state: string) => {
        if (state === 'CONNECTED') {
          clearTimeout(timeout)
          this.client.off('connection-state-change', handleState)
          resolve()
          return
        }

        if (state === 'DISCONNECTED') {
          clearTimeout(timeout)
          this.client.off('connection-state-change', handleState)
          reject(new Error('Falha ao conectar na sala'))
        }
      }

      this.client.on('connection-state-change', handleState)
    })
  }

  private async ensureVBProcessor() {
    const videoTrack = this.localTracks.videoTrack
    if (!videoTrack) return null

    if (!this.processor) {
      const processor = this.extension.createProcessor()
      await processor.init()
      // Um leave durante o init troca ou fecha a track: não plugar a antiga.
      // Processor descartado é desabilitado para não ficar ativo à toa.
      if (this.localTracks.videoTrack !== videoTrack || this.processor) {
        void Promise.resolve()
          .then(() => processor.disable())
          .catch(() => undefined)
        if (this.localTracks.videoTrack !== videoTrack) return null
      } else {
        this.processor = processor
      }
    }
    if (!this.processor) return null

    if (this.processorTrack !== videoTrack) {
      try {
        this.processorTrack?.unpipe()
      } catch {
        // no-op: track antiga pode já estar fechada
      }

      try {
        videoTrack.unpipe()
      } catch {
        // no-op: track pode ainda não ter pipeline
      }
      videoTrack.pipe(this.processor).pipe(videoTrack.processorDestination)
      this.processorTrack = videoTrack
    }

    return this.processor
  }

  private async disableVirtualBackground() {
    if (!this.processor) return
    await this.processor.disable()
  }

  private async releaseVBProcessor(
    processor: IVirtualBackgroundProcessor | null,
    processorTrack: ICameraVideoTrack | null
  ) {
    try {
      processorTrack?.unpipe()
    } catch {
      // no-op: track antiga pode já estar fechada
    }

    if (!processor) return
    try {
      await processor.disable()
    } catch {
      // no-op: processor pode já estar desabilitado
    }
  }
}

let singleton: AgoraEngine | null = null

export function getAgoraEngine() {
  if (!singleton) {
    singleton = new AgoraEngine()
  }
  return singleton
}
